/**
 * Default Properties
 * Factory functions for creating default properties for nodes and connections
 */

import type {
  NodeType,
  NodeProperties,
  BusProperties,
  GeneratorProperties,
  LoadProperties,
  StorageProperties,
  LineProperties,
  ConnectionType,
  ConnectionProperties,
} from '@/types';

// ============================================================
// DEFAULT NODE PROPERTIES
// ============================================================

/** Default properties for a bus node */
export const DEFAULT_BUS_PROPERTIES: BusProperties = {
  v_nom: 380,
  v_mag_pu: 1.0,
};

/** Default properties for a generator node */
export const DEFAULT_GENERATOR_PROPERTIES: GeneratorProperties = {
  p_nom: 100,
  fuel_type: 'gas',
  marginal_cost: 30,
};

/** Default properties for a load node */
export const DEFAULT_LOAD_PROPERTIES: LoadProperties = {
  p_set: 50,
  q_set: 0,
};

/** Default properties for a storage node */
export const DEFAULT_STORAGE_PROPERTIES: StorageProperties = {
  p_nom: 50,
  max_hours: 4,
};

// ============================================================
// DEFAULT CONNECTION PROPERTIES
// ============================================================

/** Default properties for a transmission line */
export const DEFAULT_LINE_PROPERTIES: LineProperties = {
  r: 0.01,
  x: 0.1,
  s_nom: 100,
  b: 0,
};

// ============================================================
// FACTORY FUNCTIONS
// ============================================================

/**
 * Get default properties for a node type
 * @param type - The node type
 * @returns Default properties for that node type
 */
export function getDefaultNodeProperties(type: NodeType): NodeProperties {
  switch (type) {
    case 'bus':
      return { ...DEFAULT_BUS_PROPERTIES };
    case 'generator':
      return { ...DEFAULT_GENERATOR_PROPERTIES };
    case 'load':
      return { ...DEFAULT_LOAD_PROPERTIES };
    case 'storage':
      return { ...DEFAULT_STORAGE_PROPERTIES };
    default: {
      // TypeScript exhaustive check
      const _exhaustive: never = type;
      return _exhaustive;
    }
  }
}

/**
 * Get default properties for a connection type
 * @param type - The connection type
 * @returns Default properties for that connection type
 */
export function getDefaultConnectionProperties(type: ConnectionType): ConnectionProperties {
  switch (type) {
    case 'line':
      return { ...DEFAULT_LINE_PROPERTIES };
    case 'transformer':
      // Transformer uses similar properties to line for now
      return { ...DEFAULT_LINE_PROPERTIES, s_nom: 200 };
    case 'attachment':
      return {};
    default: {
      const _exhaustive: never = type;
      return _exhaustive;
    }
  }
}

/**
 * Legacy alias for getDefaultNodeProperties
 * @deprecated Use getDefaultNodeProperties instead
 */
export const getDefaultProps = getDefaultNodeProperties;

// ============================================================
// PROPERTY MERGE HELPERS
// ============================================================

/**
 * Merge custom properties with defaults for a node type
 * @param type - The node type
 * @param customProps - Custom properties to merge
 * @returns Merged properties
 */
export function mergeWithDefaults(
  type: NodeType,
  customProps: Partial<NodeProperties>
): NodeProperties {
  return {
    ...getDefaultNodeProperties(type),
    ...customProps,
  };
}
